import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Collapse, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';

import { isRowApproved, isRowManuallyApproved } from '../../manualOverridePersistence';
import ApprovalActionButton from '../../reportCards/ApprovalActionButton';
import ApprovalStatusChip from '../../reportCards/ApprovalStatusChip';
import ManualChip from '../../reportCards/ManualChip';
import { formatPrice } from '../../shared/format';
import type { ManualOverrideState } from '../../types';

import InclusionRow, { INCLUSION_ROW_GRID_TEMPLATE_COLUMNS } from './InclusionRow';
import InclusionRowHeader from './InclusionRowHeader';
import { getDealOptionsInclusionsSearchText } from './dealOptionsInclusionsQueue';
import type { DealOptionRowView } from './types';

interface Props {
  deal: DealOptionRowView;
  expandedRows: Record<string, boolean>;
  manualOverrides: ManualOverrideState;
  searchQuery: string;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (rowId: string, defaultOpen?: boolean) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function DealCard({
  deal,
  expandedRows,
  manualOverrides,
  searchQuery,
  showAutoApproved,
  onApprovalChange,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  const isDealApproved = isRowApproved(deal, manualOverrides);
  const isDealManuallyApproved = isRowManuallyApproved(deal, manualOverrides);
  // The deal option itself counts as one more approvable item, alongside its inclusions.
  const totalItemCount = deal.inclusions.length + 1;
  const pendingCount =
    deal.inclusions.filter((inc) => !isRowApproved(inc, manualOverrides)).length + (isDealApproved ? 0 : 1);
  const isOpen = expandedRows[deal.id] ?? true;
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const visibleInclusions = deal.inclusions.filter((inc) => {
    if (!showAutoApproved && isRowApproved(inc, manualOverrides)) {
      return false;
    }
    return (
      !normalizedSearchQuery || getDealOptionsInclusionsSearchText(inc).toLowerCase().includes(normalizedSearchQuery)
    );
  });
  const hasSearchMiss = !!normalizedSearchQuery && deal.inclusions.length > 0 && visibleInclusions.length === 0;

  const metrics = [
    { label: 'Sell price', value: formatPrice(deal.sellPrice, deal.currency) },
    { label: 'Cost price', value: formatPrice(deal.costPrice, deal.currency) },
    { label: '% margin', value: deal.marginPercent != null ? `${deal.marginPercent}%` : '—' },
    { label: 'Rack rate', value: formatPrice(deal.rackRate, deal.currency) },
    { label: 'Room asset', roomAssetLinked: deal.roomAssetLinked },
  ];

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1.25, overflow: 'hidden' }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => onToggleDetails(deal.id, true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggleDetails(deal.id, true);
          }
        }}
        sx={{
          alignItems: 'center',
          columnGap: 2,
          cursor: 'pointer',
          display: 'grid',
          gridTemplateColumns: INCLUSION_ROW_GRID_TEMPLATE_COLUMNS,
          p: 2,
          '&:hover': { bgcolor: 'grey.50' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="subtitle1" fontWeight={700}>
              {deal.dealOptionName || '—'}
            </Typography>
            {deal.needsFlexiNightsReview && (
              <Tooltip title="Set flexi nights to TRUE on the package in Admin Portal">
                <WarningAmberIcon color="error" fontSize="small" onClick={(event) => event.stopPropagation()} />
              </Tooltip>
            )}
            {isDealManuallyApproved && <ManualChip />}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <ApprovalStatusChip
              approved={pendingCount === 0}
              count={pendingCount > 0 ? pendingCount : totalItemCount}
            />
            <Typography variant="body2" color="text.secondary">
              {deal.inclusions.length} inclusion{deal.inclusions.length === 1 ? '' : 's'}
            </Typography>
          </Stack>
        </Box>

        {metrics.map((metric) => (
          <Box key={metric.label} onClick={(event) => event.stopPropagation()}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ display: 'block', letterSpacing: 0.6 }}
            >
              {metric.label.toUpperCase()}
            </Typography>
            {'value' in metric && (
              <Typography variant="body2" fontWeight={600}>
                {metric.value}
              </Typography>
            )}
            {'roomAssetLinked' in metric && (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                <Box
                  component="span"
                  sx={{
                    bgcolor: metric.roomAssetLinked ? 'success.main' : 'warning.main',
                    borderRadius: '50%',
                    flexShrink: 0,
                    height: 8,
                    width: 8,
                  }}
                />
                <Typography variant="body2" fontWeight={600}>
                  {metric.roomAssetLinked ? 'Linked' : 'Unlinked'}
                </Typography>
              </Stack>
            )}
          </Box>
        ))}

        <Box onClick={(event) => event.stopPropagation()} sx={{ alignItems: 'center', display: 'flex' }}>
          <ApprovalActionButton
            approved={isDealApproved}
            onApprove={() => onApprovalChange(deal.id)}
            onUnapprove={() => onUnapprovalChange(deal.id)}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            size="small"
            sx={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={isOpen}>
        {visibleInclusions.length === 0 ? (
          <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {hasSearchMiss
                ? 'No inclusions match your search.'
                : deal.inclusions.length === 0
                  ? 'No inclusions configured.'
                  : 'All inclusions approved.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
            <InclusionRowHeader />
            {visibleInclusions.map((inc) => (
              <InclusionRow
                key={inc.id}
                currency={deal.currency}
                inc={inc}
                approved={isRowApproved(inc, manualOverrides)}
                manuallyApproved={isRowManuallyApproved(inc, manualOverrides)}
                roomName={deal.roomAssetLinked ? deal.roomName : undefined}
                onApprovalChange={onApprovalChange}
                onUnapprovalChange={onUnapprovalChange}
              />
            ))}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}
